import { Badge } from "@/components/ui/badge";

interface ArtifactVersionBadgeProps {
  uploadVersion: number | null | undefined;
  /** Whether the most recent upload added or changed this artefact. */
  highlight?: boolean;
  className?: string;
}

/** The upload version an artefact carries, highlighted when the latest upload
 *  is the one that put it there.
 *
 *  One component because the four places that showed a version had already
 *  drifted apart: the checklist highlighted anything that wasn't the string
 *  "v1", the preview dialog was always grey, and the card was plain text with
 *  no pill at all. The rule now lives here and the caller only has to say
 *  whether this artefact is from the latest upload.
 *
 *  Yellow is `brand2`, which the checklist already used — no new Badge variant
 *  was needed for this. */
const ArtifactVersionBadge = ({
  uploadVersion,
  highlight = false,
  className = "",
}: ArtifactVersionBadgeProps) => {
  // Anything stored before upload versions existed is shown as the first
  // upload, which is what it was.
  const version = uploadVersion ?? 1;

  return (
    <Badge
      variant={highlight ? "brand2" : "secondary"}
      appearance={highlight ? "solid" : "outlined"}
      // The number alone is ambiguous away from the column that labels it.
      title={highlight ? `Upload version ${version} — new or changed` : `Upload version ${version}`}
      className={`rounded-full px-2 py-0 font-normal ${className}`}
    >
      v{version}
    </Badge>
  );
};

export default ArtifactVersionBadge;
